import {
  type IndexData,
  indexDataValue,
  type YearConstants,
  yearConstantValue,
} from "../types/Codes";
import type {
  KelaModel,
  KelaPensionCalculationResult,
} from "../types/KelaModel";

const DEFAULT_LEVEL_INCREASE = 1;

export interface KelaCalculationContext {
  readonly lowestPensionAgeYear: number;
  readonly lowestPensionAgeMonth: number;
  readonly calculationYear: number;
  readonly indexData?: IndexData[];
  readonly yearConstants?: YearConstants[];
}

export function calculateKelaPension(
  model: KelaModel,
  context: KelaCalculationContext,
): KelaPensionCalculationResult {
  let preDating = 0;
  if (model.birthYear >= 1958 && model.birthYear <= 1961) {
    preDating = (65 - model.pensionAgeYear) * 12 - model.pensionAgeMonth;
  } else if (model.birthYear < 1958) {
    preDating = (65 - model.pensionAgeYear) * 12 - model.pensionAgeMonth;
  }
  if (preDating < 0) {
    preDating = 0;
  }

  let { lowestPensionAgeYear, lowestPensionAgeMonth } = context;
  if (model.birthYear < 1965) {
    lowestPensionAgeYear = 65;
    lowestPensionAgeMonth = 0;
  }

  let increaseForLateRetirement =
    (model.pensionAgeYear - lowestPensionAgeYear) * 12 +
    (model.pensionAgeMonth - lowestPensionAgeMonth);
  if (increaseForLateRetirement < 0) {
    increaseForLateRetirement = 0;
  }

  let levelIncrease = DEFAULT_LEVEL_INCREASE;

  if (!model.priceConversion) {
    levelIncrease = model.priceLevelIncrease;
  }

  const nationalPension = getNationalPension(
    model,
    context,
    increaseForLateRetirement,
    levelIncrease,
    preDating,
  );

  const minimumPensionKela =
    yearConstantValue(
      context.calculationYear,
      "vahimmaisElakeKela",
      context.yearConstants,
    ) || 0;
  const guaranteePensionLimit =
    minimumPensionKela * (1 - 0.004 * preDating) * levelIncrease;
  let guaranteePension =
    guaranteePensionLimit -
    (model.earningsRelatedPensionAndIncreaseForLateRetirement +
      nationalPension);
  if (guaranteePension < 0) {
    guaranteePension = 0;
  }

  let kelaPension = nationalPension + guaranteePension;

  const pieninMaksettavaKansanelake =
    yearConstantValue(
      context.calculationYear,
      "pieninMaksettavaKansanelake",
      context.yearConstants,
    ) || 0;
  if (kelaPension < pieninMaksettavaKansanelake * levelIncrease) {
    kelaPension = 0;
  }

  const salaryMultiplierNextYear =
    indexDataValue(
      context.calculationYear + 1,
      "Palkkakerroin",
      context.indexData,
    ) || 1;
  const teiNextYear =
    indexDataValue(context.calculationYear + 1, "TEI", context.indexData) || 1;
  const kelaPensionIndexNextYear =
    (4 / 3) * teiNextYear - (1 / 3) * salaryMultiplierNextYear;

  return {
    pension: kelaPension,
    pensionNextYear: kelaPension * kelaPensionIndexNextYear,
  };
}

const getNationalPension = (
  model: KelaModel,
  context: KelaCalculationContext,
  increaseForLateRetirement: number,
  levelIncrease: number,
  preDating: number,
): number => {
  let nationalPension = 0;

  const rajatulo =
    yearConstantValue(
      context.calculationYear,
      "rajatulo",
      context.yearConstants,
    ) || 0;
  const fullKelaPension =
    yearConstantValue(
      context.calculationYear,
      model.inRelationship
        ? "taysiKansanElakeParisuhteessa"
        : "taysiKansanElakeYksin",
      context.yearConstants,
    ) || 0;
  if (model.earningsRelatedPension - rajatulo * levelIncrease > 0) {
    nationalPension =
      (fullKelaPension * levelIncrease -
        0.5 * (model.earningsRelatedPension - rajatulo * levelIncrease)) *
      (1 - 0.004 * preDating);
  } else {
    nationalPension = fullKelaPension * levelIncrease;
  }
  if (nationalPension < 0) {
    nationalPension = 0;
  }

  if (model.birthYear < 1962) {
    nationalPension *= 1 + 0.006 * increaseForLateRetirement;
  } else {
    nationalPension *= 1 + 0.004 * increaseForLateRetirement;
  }
  return nationalPension;
};
